import { type WorkosLoginResponse } from "@mg/schemas/src/prince/auth";
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

type UIState = {
  headerHeight: number;
  searchHeight: number;
  showResolvedComments: boolean;
  showDismissedComments: boolean;
  showDeletedComments: boolean;
  authenticationDialogOpen: boolean;
  /**
   * The MFA step of an `/enterprise/switch` response (christo #6156): the
   * target enterprise requires MFA and this session carries no proof for it,
   * so the response is a TOTP step instead of a token. Set by
   * `switchEnterpriseWithMfa`; `AuthenticationDialog` reads it and opens on
   * its MFA step, which finishes the switch with the token the verify call
   * mints. Null for every other way the dialog opens.
   */
  enterpriseMfaSwitch: WorkosLoginResponse | null;
  drawerOpen: {
    app: boolean;
    comments: boolean;
  };
};

const initialState: UIState = {
  headerHeight: 0,
  searchHeight: 0,
  showResolvedComments: false,
  showDismissedComments: false,
  showDeletedComments: false,
  authenticationDialogOpen: false,
  enterpriseMfaSwitch: null,
  drawerOpen: {
    app: false,
    comments: false,
  },
};

export const UISlice = createSlice({
  name: "ui",
  initialState,
  reducers: {
    setHeaderHeight(state, action) {
      state.headerHeight = action.payload;
    },
    setSearchHeight(state, action) {
      state.searchHeight = action.payload;
    },
    setAuthenticationDialogOpen(state, action: PayloadAction<boolean>) {
      state.authenticationDialogOpen = action.payload;
    },
    /**
     * Park the enterprise-switch MFA step and open the dialog on it. The two
     * move together: the dialog has nothing to render without the step, and a
     * step with no open dialog would be a silent no-op switch.
     */
    openEnterpriseMfaSwitch(state, action: PayloadAction<WorkosLoginResponse>) {
      state.enterpriseMfaSwitch = action.payload;
      state.authenticationDialogOpen = true;
    },
    /** Drop the parked step — a cancel, or the dialog closing for any reason. */
    clearEnterpriseMfaSwitch(state) {
      state.enterpriseMfaSwitch = null;
    },
    toggleShowResolvedComments(state) {
      state.showResolvedComments = !state.showResolvedComments;
    },
    toggleShowDismissedComments(state) {
      state.showDismissedComments = !state.showDismissedComments;
    },
    toggleShowDeletedComments(state) {
      state.showDeletedComments = !state.showDeletedComments;
    },
    setDrawerOpen(
      state,
      action: PayloadAction<Partial<UIState["drawerOpen"]>>,
    ) {
      Object.assign(state.drawerOpen, action.payload);
    },
  },
});

export const {
  setHeaderHeight,
  setSearchHeight,
  setAuthenticationDialogOpen,
  openEnterpriseMfaSwitch,
  clearEnterpriseMfaSwitch,
  toggleShowResolvedComments,
  toggleShowDismissedComments,
  toggleShowDeletedComments,
  setDrawerOpen,
} = UISlice.actions;
